import React, { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, Trash2, Save, X, Users } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface PermissionItem {
  id: number;
  permission_key: string;
  display_name: string;
  description: string;
  category: string;
  route_path: string;
  icon: string;
  display_order: number;
  is_active: number;
  requires_admin: number;
  requires_developer: number;
  parent_key: string | null;
}

interface Template {
  id: number;
  template_name: string;
  description: string;
  icon: string;
  display_order: number;
  is_active: number;
}

interface TemplateWithPermissions extends Template {
  permissions: PermissionItem[];
}

const PermissionManagement: React.FC = () => {
  const [items, setItems] = useState<PermissionItem[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateWithPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<PermissionItem | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [activeTab, setActiveTab] = useState<'items' | 'templates'>('items');

  // カテゴリ別にグループ化
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, PermissionItem[]>);

  const categoryLabels: Record<string, string> = {
    core: '基本機能',
    management: '管理機能',
    line: 'LINE連携',
    admin: 'システム設定'
  };

  useEffect(() => {
    fetchItems();
    fetchTemplates();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/permissions/items');
      setItems(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError('権限項目の取得に失敗しました');
      console.error('Error fetching permission items:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/permissions/templates');
      setTemplates(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
    }
  };

  const fetchTemplateDetails = async (templateId: number) => {
    try {
      const response = await apiClient.get(`/permissions/templates/${templateId}`);
      setSelectedTemplate(response.data.data);
    } catch (err: any) {
      console.error('Error fetching template details:', err);
    }
  };

  const handleAddItem = () => {
    setIsAddingNew(true);
    setEditingItem({
      id: 0,
      permission_key: '',
      display_name: '',
      description: '',
      category: 'core',
      route_path: '',
      icon: '📋',
      display_order: 0,
      is_active: 1,
      requires_admin: 0,
      requires_developer: 0,
      parent_key: null
    });
  };

  const handleEditItem = (item: PermissionItem) => {
    setIsAddingNew(false);
    setEditingItem({ ...item });
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;

    try {
      if (isAddingNew) {
        await apiClient.post('/permissions/items', editingItem);
      } else {
        await apiClient.put(`/permissions/items/${editingItem.id}`, editingItem);
      }
      await fetchItems();
      setEditingItem(null);
      setIsAddingNew(false);
    } catch (err: any) {
      setError('保存に失敗しました');
      console.error('Error saving item:', err);
    }
  };

  const handleDeleteItem = async (id: number) => {
    if (!confirm('この権限項目を削除しますか？')) return;

    try {
      await apiClient.delete(`/permissions/items/${id}`);
      await fetchItems();
    } catch (err: any) {
      setError('削除に失敗しました');
      console.error('Error deleting item:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setIsAddingNew(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 開発者メモ */}
      <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
        <div className="flex items-start gap-2">
          <span className="text-xl mt-0.5">&#9888;&#65039;</span>
          <div>
            <p className="font-bold text-amber-800 mb-1">開発者メモ</p>
            <p className="text-amber-700 text-sm leading-relaxed">
              この画面での設定変更は、現在DB（permission_items）の更新のみ行われます。
              実際のサイドバーやホーム画面の表示制御、およびアクセス制限（ProtectedRoute）には反映されません。
              項目の出し入れや権限の変更を行う場合は、
              <code className="bg-amber-100 px-1 rounded text-xs">AccordionSidebarMenu.tsx</code> および
              <code className="bg-amber-100 px-1 rounded text-xs">DashboardV3.tsx</code> のソースコードを直接修正してください。
            </p>
          </div>
        </div>
      </div>

      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800">権限管理システム</h1>
        </div>
        <p className="text-gray-600">
          開発者専用：権限項目の追加・編集・削除、テンプレート管理
        </p>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* タブ切り替え */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('items')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'items'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            権限項目管理
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'templates'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            テンプレート管理
          </button>
        </div>
      </div>

      {/* 権限項目管理タブ */}
      {activeTab === 'items' && (
        <div>
          {/* 追加ボタン */}
          <div className="mb-4 flex justify-end">
            <button
              onClick={handleAddItem}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              新規権限項目を追加
            </button>
          </div>

          {/* 編集フォーム */}
          {editingItem && (
            <div className="mb-6 p-6 bg-white border-2 border-blue-500 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">
                {isAddingNew ? '新規権限項目' : '権限項目を編集'}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    権限キー *
                  </label>
                  <input
                    type="text"
                    value={editingItem.permission_key}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, permission_key: e.target.value })
                    }
                    disabled={!isAddingNew}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="例: sales_management"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    表示名 *
                  </label>
                  <input
                    type="text"
                    value={editingItem.display_name}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, display_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="例: 売上管理"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    説明
                  </label>
                  <input
                    type="text"
                    value={editingItem.description}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="例: 売上データの閲覧・分析"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    カテゴリ *
                  </label>
                  <select
                    value={editingItem.category}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, category: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="core">基本機能</option>
                    <option value="management">管理機能</option>
                    <option value="line">LINE連携</option>
                    <option value="admin">システム設定</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ルートパス
                  </label>
                  <input
                    type="text"
                    value={editingItem.route_path}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, route_path: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="例: /sales-management"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    アイコン
                  </label>
                  <input
                    type="text"
                    value={editingItem.icon}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, icon: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="例: 💰"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    表示順序
                  </label>
                  <input
                    type="number"
                    value={editingItem.display_order}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        display_order: parseInt(e.target.value) || 0
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingItem.requires_admin === 1}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            requires_admin: e.target.checked ? 1 : 0
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm">管理者権限必須</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingItem.requires_developer === 1}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            requires_developer: e.target.checked ? 1 : 0
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm">開発者専用</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSaveItem}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                  保存
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  <X className="w-4 h-4" />
                  キャンセル
                </button>
              </div>
            </div>
          )}

          {/* 権限項目一覧（カテゴリ別） */}
          <div className="space-y-6">
            {Object.entries(groupedItems).map(([category, categoryItems]) => (
              <div key={category} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-4 text-gray-800">
                  {categoryLabels[category] || category}
                </h3>
                <div className="space-y-2">
                  {categoryItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <span className="text-2xl">{item.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-800">
                              {item.display_name}
                            </h4>
                            {item.requires_admin === 1 && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                                管理者
                              </span>
                            )}
                            {item.requires_developer === 1 && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded">
                                開発者
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{item.description}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            キー: {item.permission_key} | パス: {item.route_path}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* テンプレート管理タブ */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-2 gap-6">
          {/* テンプレート一覧 */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800">テンプレート一覧</h3>
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => fetchTemplateDetails(template.id)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                  selectedTemplate?.id === template.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{template.icon}</span>
                  <div>
                    <h4 className="font-bold text-gray-800">{template.template_name}</h4>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* テンプレート詳細 */}
          <div>
            {selectedTemplate ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{selectedTemplate.icon}</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {selectedTemplate.template_name}
                    </h3>
                    <p className="text-sm text-gray-600">{selectedTemplate.description}</p>
                  </div>
                </div>
                <h4 className="font-semibold text-gray-700 mb-3">
                  含まれる権限（{selectedTemplate.permissions.length}個）
                </h4>
                <div className="space-y-2">
                  {selectedTemplate.permissions.map((perm) => (
                    <div
                      key={perm.id}
                      className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg"
                    >
                      <span className="text-xl">{perm.icon}</span>
                      <div>
                        <p className="font-medium text-gray-800">{perm.display_name}</p>
                        <p className="text-xs text-gray-500">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-12 text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  左からテンプレートを選択してください
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagement;
